import { CustomerRoleName } from '@dtp/membership-service-types/dist/customerInformation/CustomerRoleNameTypes';
import { CustomerMembershipStatus } from '@dtp/membership-service-types';
import { useMemo } from 'react';
import useSelector from '../redux/typedHooks';
import { ProductID } from '../lib/ProductID';
import { useLocalStorage } from './useLocalStorage';
import { ServiceStatus } from '../../../types/customerInformationType';

export const useMembershipStatus = () => {
  const customer = useSelector((state) => state.myMembership.customerInformation.data?.customer);
  const membership = useSelector((state) => state.myMembership.customerInformation.data?.membership);
  const households = useSelector((state) => state.myMembership.customerInformation.data?.households || []);
  const services = useSelector((state) => state.myMembership.customerInformation.data?.services);
  const [isMembershipTerminatedInLocalStorage] = useLocalStorage(`${customer?.customerId || ''}_terminate`, false);

  const household = useMemo(() => households[0], [households]);

  const isMainHouseholdMember = useMemo(
    () => household?.customerRoles.includes(CustomerRoleName.MAIN_HOUSEHOLD_MEMBER) || false,
    [household],
  );

  const isHouseholdMember = useMemo(
    () => household?.customerRoles?.includes(CustomerRoleName.HOUSEHOLD_MEMBER) || false,
    [household?.customerRoles],
  );

  const isPayer = useMemo(() => household?.isPayer, [household?.isPayer]);

  const isMembershipActive = useMemo(
    () => membership?.status === CustomerMembershipStatus.ActiveMembership,
    [membership?.status],
  );

  const isMembershipValid = useMemo(() => membership?.isValid, [membership?.isValid]);

  const isMembershipCancelled = useMemo(
    () => membership?.status === CustomerMembershipStatus.CancelledMembership,
    [membership?.status],
  );

  const isMembershipExpired = useMemo(
    () => membership?.status === CustomerMembershipStatus.ExpiredMembership,
    [membership],
  );

  const canAdministrateMembership = useMemo(
    () =>
      customer?.customerRoleName === CustomerRoleName.STANDALONE_MEMBER ||
      customer?.customerRoleName === CustomerRoleName.MAIN_HOUSEHOLD_MEMBER,
    [customer?.customerRoleName],
  );

  const canAddHouseholdMembers = useMemo(() => household?.canAddHouseholdMembers, [household?.canAddHouseholdMembers]);
  const canRemoveHouseholdMembers = useMemo(() => canAddHouseholdMembers, [canAddHouseholdMembers]);

  const canAddRoadsideAssistance = useMemo(() => {
    if (!isMembershipActive) return false;

    const raProduct = services?.find((s) => s.product?.id === ProductID.RoadSideAssistance);
    return raProduct?.serviceStatus === ServiceStatus.CanBeAdded;
  }, [isMembershipActive, services]);

  const canRemoveRoadsideAssistance = useMemo(() => {
    if (!isMembershipActive) return false;

    if (
      customer?.customerRoleName === CustomerRoleName.STANDALONE_MEMBER &&
      membership?.product?.id !== ProductID.TrialMembership
    )
      return true;

    return household?.householdChanges?.globalChanges?.downgradeProducts?.some(
      (p) => p.id === ProductID.RoadSideAssistance,
    );
  }, [
    isMembershipActive,
    customer?.customerRoleName,
    membership?.product?.id,
    household?.householdChanges?.globalChanges?.downgradeProducts,
  ]);

  const canRemoveNafMC = useMemo(() => {
    if (!isMembershipActive) return false;

    return canAdministrateMembership;
  }, [isMembershipActive, canAdministrateMembership]);

  const canTerminateMembership = useMemo(() => {
    if (
      canAdministrateMembership &&
      [CustomerMembershipStatus.ActiveMembership, CustomerMembershipStatus.ExpiredMembership].includes(
        membership?.status,
      ) &&
      !isMembershipTerminatedInLocalStorage
    )
      return true;

    return false;
  }, [canAdministrateMembership, isMembershipTerminatedInLocalStorage, membership?.status]);

  return {
    isMainHouseholdMember,
    isHouseholdMember,
    isPayer,
    isMembershipActive,
    isMembershipValid,
    isMembershipCancelled,
    isMembershipExpired,
    canAdministrateMembership,
    canAddHouseholdMembers,
    canRemoveHouseholdMembers,
    canAddRoadsideAssistance,
    canRemoveRoadsideAssistance,
    canRemoveNafMC,
    canTerminateMembership,
  };
};
